import { FC } from "react";
import { Box, Grid } from "@mui/material";
import { DashboardStatCard } from "@/features/dashboard/components/DashboardStatCard";
import { background } from "@/theme/colors";
import { TGetCountSummary } from "@/api/services/main/dashboard/dtos/get-count-summary.dto";
import { TMonthlyTarget } from "@/api/services/main/dashboard/dtos/monthly-target.dto";
import { LoadingArea } from "@/components/elements/Loading/Loading";

type TDashboardMonthlyStatisticsProps = {
  countSummaryData?: TGetCountSummary;
  isLoadingCountSummary?: boolean;
  monthlyTargetData?: TMonthlyTarget;
  isLoadingMonthlyTarget?: boolean;
};

export const DashboardMonthlyStatistics: FC<TDashboardMonthlyStatisticsProps> = ({
  countSummaryData,
  isLoadingCountSummary,
  monthlyTargetData,
  isLoadingMonthlyTarget,
}) => {
  if (isLoadingCountSummary || isLoadingMonthlyTarget) {
    return <LoadingArea />;
  }

  // Show openJobs column only when BE returns openJobs data (current month only)
  const showOpenJobs = countSummaryData?.currentMonth.openJobs != null;

  return (
    <Box
      mt={2}
      sx={{
        p: "16px 32px",
        border: `1px solid ${background.disable}`,
        borderRadius: "8px",
        backgroundColor: background.white,
        boxSizing: "border-box",
      }}
    >
      <Grid container spacing={10}>
        <Grid item xs={showOpenJobs ? 6 : 8}>
          <Grid container spacing={5}>
            <Grid item xs={6}>
              <DashboardStatCard
                label="今月の新規求人数"
                current={countSummaryData?.currentMonth.jobs ?? 0}
                target={monthlyTargetData?.targetJobs}
                previous={countSummaryData?.previousMonth.jobs ?? 0}
                unit="件"
                isTarget={true}
                tooltipInfo="募集開始日が指定年月内の求人をカウント（未設定の場合は作成日時で判定）"
              />
            </Grid>
            <Grid item xs={6}>
              <DashboardStatCard
                label="今月の新規案件数"
                current={countSummaryData?.currentMonth.cases ?? 0}
                target={monthlyTargetData?.targetCases}
                previous={countSummaryData?.previousMonth.cases ?? 0}
                unit="件"
                isTarget={true}
                showDividerRight={true}
                tooltipInfo="求人から作成された案件の作成日が指定年月内のものをカウント"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={showOpenJobs ? 3 : 4}>
          <DashboardStatCard
            label="今月の新規登録人材数"
            current={countSummaryData?.currentMonth.talents ?? 0}
            target={monthlyTargetData?.targetTalents}
            previous={countSummaryData?.previousMonth.talents ?? 0}
            unit="人"
            isTarget={true}
            showDividerRight={showOpenJobs}
            tooltipInfo="人材の作成日時が指定年月内のものをカウント"
          />
        </Grid>
        {showOpenJobs && (
          <Grid item xs={3}>
            <DashboardStatCard
              label="募集中求人数"
              current={countSummaryData?.currentMonth.openJobs ?? 0}
              target={monthlyTargetData?.targetOpenJobs}
              unit="件"
              isTarget={true}
              tooltipInfo="作成日時が指定年月末日以前、かつステータスが「募集中」の求人をカウント"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
